import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { motion, AnimatePresence } from 'framer-motion';

const BACKGROUND_IMAGE = '/bf1d413dc5dccd4653455a1137e7ff31cca85cf1.png';
const PLAY_ICON_SVG = '/d12622fb2dd39f48991839fe40020f16252736fd.svg';

export const HeroSection: React.FC = () => {
  const { t } = useTranslation(['landing']);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  return (
    <Box
      component="section"
      id="hero"
      sx={{
        position: 'relative',
        width: '100%',
        minHeight: { xs: '80vh', md: '100vh' },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#003D4D',
        overflow: 'hidden',
      }}
    >
      <AnimatePresence>
        {!isVideoPlaying && (
          <motion.div
            key="hero-content"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
            style={{ width: '100%', height: '100%', position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                '& img': {
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'right 35%',
                  transform: 'scale(1.15) translateY(5%)',
                },
              }}
            >
              <img 
                src={BACKGROUND_IMAGE}
                alt="" 
                loading="eager"
              />
            </Box>

            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(to right, #003D4D 0%, rgba(0,61,77,0.8) 50%, rgba(0,61,77,0) 100%)',
              }}
            />

            <Box
              sx={{
                position: 'relative',
                zIndex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                justifyContent: 'center',
                width: '100%',
                maxWidth: 1280,
                px: { xs: 3, md: 6 },
                py: { xs: 12, md: 16 },
              }}
            >
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            backgroundColor: 'rgba(140, 243, 243, 0.2)',
            borderRadius: '9999px',
            px: 2,
            py: 0.5,
            mb: 3,
          }}
        >
          <Typography
            sx={{
              fontFamily: '"New Order", sans-serif',
              fontWeight: 700,
              fontSize: '12px',
              color: '#8CF3F3',
              letterSpacing: '1.2px',
              textTransform: 'uppercase',
            }}
          >
            {t('landing:hero.badge')}
          </Typography>
        </Box>

        <Box
          sx={{
            mb: 3,
          }}
        >
          <Typography
            sx={{
              fontFamily: '"New Order", sans-serif',
              fontWeight: 500,
              fontSize: { xs: '36px', md: '60px' },
              lineHeight: { xs: '42px', md: '66px' },
              color: '#FFFFFF',
            }}
          >
            {t('landing:hero.title_line1')}
          </Typography>
          <Typography
            sx={{
              fontFamily: '"New Order", sans-serif',
              fontWeight: 500,
              fontSize: { xs: '36px', md: '60px' },
              lineHeight: { xs: '42px', md: '66px' },
              color: '#FFFFFF',
              '& span': {
                fontWeight: 600,
                color: '#8CF3F3',
              },
            }}
          >
            {t('landing:hero.title_line2_prefix')}<span>{t('landing:hero.title_line2_highlight')}</span>
          </Typography>
          <Typography
            sx={{
              fontFamily: '"New Order", sans-serif',
              fontWeight: 500,
              fontSize: { xs: '36px', md: '60px' },
              lineHeight: { xs: '42px', md: '66px' },
              color: '#FFFFFF',
            }}
          >
            {t('landing:hero.title_line3')}
          </Typography>
        </Box>

        <Box
          sx={{
            maxWidth: 512,
            mb: 3,
          }}
        >
          <Typography
            sx={{
              fontFamily: '"New Order", sans-serif',
              fontSize: '16px',
              lineHeight: '24px',
              color: 'rgba(255, 255, 255, 0.8)',
            }}
          >
            {t('landing:hero.description')}
          </Typography>
        </Box>

        {/* 
        <Box>
          <Box
            component="button"
            onClick={() => setIsVideoPlaying(true)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              backgroundColor: 'transparent',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '12px',
              px: { xs: 3, md: 4 },
              py: { xs: 1.5, md: 2 },
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderColor: 'rgba(255, 255, 255, 0.5)',
              },
            }}
          >
            <Box
              sx={{
                width: 20,
                height: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '& svg': {
                  width: '100%',
                  height: '100%',
                  fill: '#FFFFFF',
                },
              }}
            >
              <img 
                src={PLAY_ICON_SVG} 
                alt="" 
              />
            </Box>
            <Typography
              sx={{
                fontFamily: '"New Order", sans-serif',
                fontWeight: 500,
                fontSize: '16px',
                color: '#FFFFFF',
                whiteSpace: 'nowrap',
              }}
            >
              {t('landing:hero.watch_video')}
            </Typography>
          </Box>
        </Box>
        */}
        </Box>
      </motion.div>
      )}
      </AnimatePresence>

      <AnimatePresence>
        {isVideoPlaying && (
          <motion.div
            key="hero-video"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            style={{ width: '100%', height: '100%', position: 'absolute', inset: 0, zIndex: 10 }}
          >
            <Box sx={{ width: '100%', height: '100%', backgroundColor: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
              <iframe
                width="100%"
                height="100%"
                src="https://www.youtube.com/embed/Be_XGaETNqk?autoplay=1"
                title="YouTube video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{ border: 0, width: '100%', height: '100%' }}
              ></iframe>
              <IconButton
                onClick={() => setIsVideoPlaying(false)}
                sx={{
                  position: 'absolute',
                  top: { xs: 80, md: 100 },
                  right: { xs: 16, md: 40 },
                  color: '#fff',
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  zIndex: 20,
                  width: 48,
                  height: 48,
                  backdropFilter: 'blur(4px)',
                  transition: 'all 0.2s ease',
                  '&:hover': { 
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    transform: 'scale(1.1)'
                  }
                }}
              >
                <CloseIcon sx={{ fontSize: 28 }} />
              </IconButton>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
};